import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, CheckCircle2, ShieldCheck } from "lucide-react";

export const PromoClaimCard = ({ onClaimed }) => {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("phone"); // phone | code
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/promo/status");
      setStatus(data);
    } catch {
      setStatus({ enabled: false });
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const sendOtp = async () => {
    if (!phone.trim()) return toast.error("Enter your mobile number.");
    setBusy(true);
    try {
      await api.post("/promo/send-otp", { phone });
      toast.success("Verification code sent by SMS.");
      setStep("code");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the code.");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!code.trim()) return toast.error("Enter the code you received.");
    setBusy(true);
    try {
      const { data } = await api.post("/promo/verify-otp", { phone, code });
      toast.success(`${money(data?.promo?.amount || status?.amount || 20)} promo credited! 🎉`);
      await refresh();
      await loadStatus();
      onClaimed?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  // Already claimed — celebratory confirmation
  if (status.claimed) {
    return (
      <div data-testid="promo-claimed-card" className="rounded-2xl border border-green-200 bg-green-50 p-5">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-600 text-white">
            <CheckCircle2 className="h-4.5 w-4.5" />
          </div>
          <div>
            <h3 className="font-heading text-lg leading-none text-green-900" style={{ fontWeight: 700 }}>
              Launch promo claimed
            </h3>
            <p className="mt-1 text-xs text-green-800">
              Your {money(status.amount)} promo credit is in your wallet. It can be spent on deal fees but can't be cashed out.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not eligible (disabled, window closed, or pool exhausted) — render nothing
  if (!status.enabled || !status.window_open || (status.remaining ?? 0) <= 0) return null;

  return (
    <div data-testid="promo-claim-card" className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Gift className="h-4.5 w-4.5" />
        </div>
        <div>
          <h3 className="font-heading text-lg leading-none" style={{ fontWeight: 700 }}>
            Claim your {money(status.amount)} launch promo
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Verify your phone to unlock {money(status.amount)} of promo credit for deal fees.
            {typeof status.remaining === "number" && (
              <span className="ml-1 font-medium text-foreground">{status.remaining} left</span>
            )}
          </p>
        </div>
      </div>

      {!status.twilio_ready ? (
        <p data-testid="promo-twilio-not-ready" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Phone verification is being set up. Check back soon to claim your promo credit.
        </p>
      ) : step === "phone" ? (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="promo-phone" className="text-xs">Mobile number</Label>
            <Input
              id="promo-phone" data-testid="promo-phone-input" type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" className="mt-1"
            />
          </div>
          <Button data-testid="promo-send-otp-btn" onClick={sendOtp} disabled={busy} className="rounded-full">
            {busy ? "Sending…" : "Send code"}
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="promo-code" className="text-xs">Enter the 6-digit code sent to {phone}</Label>
            <Input
              id="promo-code" data-testid="promo-code-input" inputMode="numeric" value={code}
              onChange={(e) => setCode(e.target.value)} placeholder="123456" className="mt-1"
            />
          </div>
          <div className="flex gap-2">
            <Button data-testid="promo-verify-otp-btn" onClick={verifyOtp} disabled={busy} className="rounded-full">
              {busy ? "Verifying…" : "Verify & claim"}
            </Button>
            <Button
              variant="ghost" data-testid="promo-change-phone-btn"
              onClick={() => { setStep("phone"); setCode(""); }} disabled={busy} className="rounded-full"
            >
              Change number
            </Button>
          </div>
        </div>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> One promo per verified phone number. Promo credit is non-withdrawable.
      </p>
    </div>
  );
};

export default PromoClaimCard;
